import {
  AIControlLoop,
  AIEquipmentInScope,
  AIInstrument,
  AILine,
  AITieIn,
  PnidAIData,
} from '../types';

const isRecord = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toArray = <T>(value: any): T[] => (Array.isArray(value) ? value : []);

const toString = (value: any): string => {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value : String(value);
};

const toOptionalString = (value: any): string | null => {
  if (value === undefined || value === null) return null;
  return typeof value === 'string' ? value : String(value);
};

const normalizeEquipment = (eq: any): AIEquipmentInScope | null => {
  const tag = toString(eq?.tag || eq?.equipment_no);
  if (!tag) return null;

  const name = toString(eq?.name || eq?.description);
  const type = toString(eq?.type || eq?.equipment_type);

  const details: Record<string, any> = isRecord(eq?.details) ? { ...eq.details } : {};
  const tableFields = toArray<{ label?: string; value?: any }>(eq?.table_fields);
  for (const field of tableFields) {
    if (!field?.label || field.label in details) continue;
    details[field.label] = field.value ?? '';
  }

  const extraKeys = ['design_pressure', 'design_temperature', 'design_capacity', 'size'];
  for (const key of extraKeys) {
    if (eq?.[key] != null && !(key in details)) {
      details[key] = eq[key];
    }
  }

  const instruments = Array.isArray(eq?.instruments)
    ? eq.instruments
    : Array.isArray(eq?.ai_instruments)
    ? eq.ai_instruments
    : [];

  return {
    tag,
    name,
    type,
    details,
    instruments: instruments.filter(Boolean),
  };
};

const normalizeTieIns = (rawTieIns: any): AITieIn[] =>
  toArray<any>(rawTieIns)
    .map((tie): AITieIn | null => {
      const tieId = toString(tie?.tie_in_id || tie?.id);
      if (!tieId) return null;

      const equipmentTag = toString(
        tie?.from ||
          tie?.from_equipment_tag ||
          tie?.to_equipment_tag ||
          tie?.connects_to?.equipment_tag
      );

      const service = toString(
        tie?.service || tie?.connects_to?.line_or_header || tie?.connected_line_number
      );

      return {
        tie_in_id: tieId,
        direction: toString(tie?.direction),
        from: toString(tie?.from || equipmentTag) || null,
        from_equipment_tag: toString(tie?.from_equipment_tag || equipmentTag) || null,
        to_equipment_tag: toString(tie?.to_equipment_tag) || null,
        service: service || null,
      };
    })
    .filter((tie): tie is AITieIn => tie !== null);

const normalizeExternalRefs = (
  rawRefs: any,
  tieIns: AITieIn[]
): PnidAIData['external_equipment_refs'] => {
  const refs: PnidAIData['external_equipment_refs'] = [];
  const seen = new Set<string>();
  const reasonByTag = new Map<string, string>();

  for (const tie of tieIns) {
    const tag = tie.from || tie.from_equipment_tag || tie.to_equipment_tag;
    if (!tag || reasonByTag.has(tag)) continue;
    const reason = tie.service || (tie.direction ? `Tie-in: ${tie.direction}` : '');
    reasonByTag.set(tag, reason);
  }

  for (const ref of toArray<any>(rawRefs)) {
    const tag = typeof ref === 'string'
      ? ref
      : toString(ref?.tag || ref?.stream_no || ref?.line_no || ref?.external_ref || ref?.equipment_tag);
    const direction = typeof ref === 'string' ? null : toOptionalString(ref?.direction);
    const externalRef = typeof ref === 'string' ? null : toOptionalString(ref?.external_ref);
    const externalPnid = typeof ref === 'string'
      ? null
      : toOptionalString(ref?.external_pnid || ref?.external_drawing);
    const key = `${direction || ''}:${tag}:${externalRef || ''}:${externalPnid || ''}`;
    if (!tag || seen.has(key)) continue;
    const reason = typeof ref === 'string' ? '' : toString(ref?.reason || ref?.context || ref?.service);
    refs.push({
      tag,
      reason: reason || reasonByTag.get(tag) || '',
      pnid_name: typeof ref === 'string'
        ? null
        : toOptionalString(ref?.pnid_name || ref?.pnid || ref?.pfd_no || ref?.drawing_no || externalPnid),
      source_ref: typeof ref === 'string' ? null : toOptionalString(ref?.source_ref),
      source_pnid: typeof ref === 'string' ? null : toOptionalString(ref?.source_pnid),
      dest_ref: typeof ref === 'string' ? null : toOptionalString(ref?.dest_ref),
      dest_pnid: typeof ref === 'string' ? null : toOptionalString(ref?.dest_pnid),
      line_no: typeof ref === 'string' ? null : toOptionalString(ref?.line_no || ref?.stream_no),
      stream_no: typeof ref === 'string' ? null : toOptionalString(ref?.stream_no || ref?.line_no),
      direction,
      external_ref: externalRef,
      external_pnid: externalPnid,
      external_drawing: typeof ref === 'string' ? null : toOptionalString(ref?.external_drawing || externalPnid),
      external_equipment: typeof ref === 'string' ? null : toOptionalString(ref?.external_equipment),
      local_ref: typeof ref === 'string' ? null : toOptionalString(ref?.local_ref),
      service: typeof ref === 'string' ? null : toOptionalString(ref?.service),
      context: typeof ref === 'string' ? null : toOptionalString(ref?.context),
    });
    seen.add(key);
  }

  for (const [tag, reason] of reasonByTag.entries()) {
    if (seen.has(tag)) continue;
    refs.push({ tag, reason: reason || '' });
    seen.add(tag);
  }

  return refs;
};

const normalizeLines = (rawLines: any): AILine[] =>
  toArray<any>(rawLines)
    .map((line): AILine | null => {
      const raw = toString(line?.raw || line?.line_number);
      if (!raw) return null;

      return {
        raw,
        size_in: toOptionalString(line?.size_in ?? line?.size),
        service_code: toOptionalString(line?.service_code),
        sequence_no: toOptionalString(line?.sequence_no ?? line?.sequence_number),
        pipe_class: toOptionalString(line?.pipe_class ?? line?.spec_or_class),
        remark: toOptionalString(line?.remark ?? line?.suffix),
      };
    })
    .filter((line): line is AILine => line !== null);

const normalizeInstruments = (rawInstruments: any): AIInstrument[] =>
  toArray<any>(rawInstruments)
    .map((inst): AIInstrument | null => {
      const tag = toString(inst?.tag);
      if (!tag) return null;
      const prefix = toString(inst?.prefix) || tag.split(/[-\s]/)[0] || '';
      const associated = toString(inst?.associated_equipment_tag || inst?.location || inst?.equipment_tag);
      return {
        tag,
        prefix,
        associated_equipment_tag: associated || null,
        loop_id: toOptionalString(inst?.loop_id),
      };
    })
    .filter((inst): inst is AIInstrument => inst !== null);

const normalizeControlLoops = (rawLoops: any): AIControlLoop[] =>
  toArray<any>(rawLoops)
    .map((loop) => {
      const loopId = toString(loop?.loop_id || loop?.id);
      if (!loopId) return null;
      return {
        loop_id: loopId,
        elements: toArray<string>(loop?.elements),
      };
    })
    .filter((loop): loop is AIControlLoop => Boolean(loop));

const normalizePageImage = (raw: any): PnidAIData['page_image'] | undefined => {
  if (!isRecord(raw)) return undefined;
  const width = Number(raw.width_px ?? raw.width);
  const height = Number(raw.height_px ?? raw.height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return undefined;
  return {
    width_px: width,
    height_px: height,
    render_scale: Number.isFinite(Number(raw.render_scale)) ? Number(raw.render_scale) : undefined,
    dpi: raw.dpi ?? null,
    coordinate_system: typeof raw.coordinate_system === 'string' ? raw.coordinate_system : undefined,
  };
};

const emptyAiData = (): PnidAIData => ({
  pid: { number: '', title: '' },
  page_image: undefined,
  line_segments: [],
  equipment_in_scope: [],
  external_equipment_refs: [],
  lines: [],
  tie_ins: [],
  instruments: [],
  control_loops: [],
  notes: [],
  source: {},
});

export const normalizeAiData = (raw: any): PnidAIData => {
  if (!isRecord(raw)) return emptyAiData();

  const tie_ins = normalizeTieIns(raw.tie_ins);
  const external_equipment_refs = normalizeExternalRefs(raw.external_equipment_refs, tie_ins);

  const pidNumber = toString(raw?.pid?.number || raw?.p_and_id_number);
  const pidTitle = toString(raw?.pid?.title || raw?.drawing_metadata?.title);

  return {
    pid: { number: pidNumber, title: pidTitle },
    page_image: normalizePageImage(raw.page_image || raw.pageImage),
    line_segments: toArray<any>(raw.line_segments).map((s) => ({
      id: toString(s?.id),
      x1: Number(s?.x1),
      y1: Number(s?.y1),
      x2: Number(s?.x2),
      y2: Number(s?.y2),
      kind: toOptionalString(s?.kind),
      confidence: s?.confidence ?? null,
    })).filter((s) => s.id && Number.isFinite(s.x1) && Number.isFinite(s.y1) && Number.isFinite(s.x2) && Number.isFinite(s.y2)),
    equipment_in_scope: toArray<any>(raw.equipment_in_scope)
      .map(normalizeEquipment)
      .filter((eq): eq is AIEquipmentInScope => Boolean(eq)),
    external_equipment_refs,
    lines: normalizeLines(raw.lines),
    tie_ins,
    instruments: normalizeInstruments(raw.instruments),
    control_loops: normalizeControlLoops(raw.control_loops),
    notes: toArray<string>(raw.notes),
    source: isRecord(raw.source) ? raw.source : {},
  };
};
